import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { CheckCircle2 } from "lucide-react";

import { ApiError, formatDateTime } from "../../lib/api";
import { useToast } from "../../components/Toast";
import { Modal } from "../../components/Modal";
import { Button, ConfirmDialog, Input, Textarea } from "../../components/ui";
import {
  TIPO_LABELS,
  folioAtencion,
  responderAtencion,
  type Atencion,
  type ResponderInput,
} from "./api";
import { fechaDMA, fechaLocalISO, hoyLocalISO } from "../../lib/fechaLocal";

const FORM_ID = "responder-atencion-form";

type Resultado = "Resuelto" | "Denegado";

const RESULTADO_LABEL: Record<Resultado, string> = {
  Resuelto: "Resuelto favorablemente",
  Denegado: "Denegado",
};

export function ResponderAtencionModal({
  atencion,
  onClose,
  onResponded,
}: {
  atencion: Atencion;
  onClose: () => void;
  onResponded: () => void;
}): JSX.Element {
  const toast = useToast();
  const hoy = hoyLocalISO();
  const fechaIngreso = fechaLocalISO(new Date(atencion.fecha_ingreso));
  const [estado, setEstado] = useState<Resultado>("Resuelto");
  const [tipoSolucion, setTipoSolucion] = useState("");
  const [fechaRespuesta, setFechaRespuesta] = useState(hoy);
  const [confirmar, setConfirmar] = useState(false);

  const mut = useMutation({
    mutationFn: (input: ResponderInput) => responderAtencion(atencion.id, input),
    onSuccess: () => {
      onResponded();
    },
    onError: (err: unknown) => {
      const msg = err instanceof ApiError ? err.message : "No se pudo cerrar la atención";
      toast.error(msg);
    },
  });

  const fechaError =
    !fechaRespuesta
      ? "Indica la fecha de respuesta."
      : fechaRespuesta > hoy
        ? "La fecha de respuesta no puede ser futura."
        : fechaRespuesta < fechaIngreso
          ? "La respuesta no puede ser anterior al ingreso."
          : undefined;

  function onSubmit(e: React.FormEvent): void {
    e.preventDefault();
    if (tipoSolucion.trim().length === 0) {
      toast.error("Describe qué se hizo para atender el caso.");
      return;
    }
    if (fechaError) return;
    setConfirmar(true);
  }

  function cerrar(): void {
    setConfirmar(false);
    mut.mutate({
      estado,
      tipo_solucion: tipoSolucion.trim(),
      // Hoy: la hora la pone el servidor. Un día anterior se registra al
      // final de ese día (no futuro y nunca antes del ingreso).
      fecha_respuesta: fechaRespuesta === hoy ? null : `${fechaRespuesta}T23:59:00`,
    });
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Responder atención"
      subtitle={`N° ${folioAtencion(atencion)} · ${TIPO_LABELS[atencion.tipo]} · ${formatDateTime(atencion.fecha_ingreso)}`}
      icon={CheckCircle2}
      size="lg"
      closeOnBackdrop={!mut.isPending}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mut.isPending}>
            Cancelar
          </Button>
          <Button type="submit" form={FORM_ID} disabled={mut.isPending}>
            {mut.isPending ? "Cerrando…" : "Cerrar atención"}
          </Button>
        </>
      }
    >
      <form id={FORM_ID} onSubmit={onSubmit} className="space-y-4">
        <div className="rounded-md bg-neutral-100 p-3 text-sm text-neutral-700">
          <div className="text-xs font-medium uppercase tracking-wider text-neutral-600">
            Solicitante
          </div>
          <div className="mt-1">
            {atencion.usuario_nombre}
            {atencion.usuario_rut && ` · ${atencion.usuario_rut}`}
          </div>
          <div className="mt-2 text-xs font-medium uppercase tracking-wider text-neutral-600">
            Descripción
          </div>
          <p className="mt-1 whitespace-pre-wrap">{atencion.descripcion}</p>
        </div>

        <fieldset>
          <legend className="mb-1 block text-xs font-medium text-neutral-700">
            Resultado
          </legend>
          <div className="flex flex-wrap gap-2">
            <RadioPill
              checked={estado === "Resuelto"}
              onClick={() => setEstado("Resuelto")}
              tone="emerald"
            >
              {RESULTADO_LABEL.Resuelto}
            </RadioPill>
            <RadioPill
              checked={estado === "Denegado"}
              onClick={() => setEstado("Denegado")}
              tone="neutral"
            >
              {RESULTADO_LABEL.Denegado}
            </RadioPill>
          </div>
        </fieldset>

        <Input
          label="Fecha de respuesta"
          type="date"
          value={fechaRespuesta}
          min={fechaIngreso}
          max={hoy}
          onChange={(e) => setFechaRespuesta(e.target.value)}
          error={fechaError}
          containerClassName="w-48"
        />

        <Textarea
          label="Solución entregada"
          required
          value={tipoSolucion}
          onChange={(e) => setTipoSolucion(e.target.value)}
          rows={4}
          placeholder="Visita técnica realizada, medidor reemplazado, llamada aclaratoria, etc."
          hint="Qué se hizo para atender el caso. Queda en el registro que revisa la SISS."
        />
      </form>

      <ConfirmDialog
        open={confirmar}
        tone="default"
        title={`Cerrar atención N° ${folioAtencion(atencion)}`}
        description={
          <>
            Se cerrará la atención de <strong>{atencion.usuario_nombre}</strong> como{" "}
            <strong>{RESULTADO_LABEL[estado].toLowerCase()}</strong>, con fecha{" "}
            <strong>{fechaDMA(fechaRespuesta)}</strong>. Una atención cerrada no se
            puede reabrir ni editar.
          </>
        }
        confirmLabel="Cerrar atención"
        loading={mut.isPending}
        onConfirm={cerrar}
        onCancel={() => setConfirmar(false)}
      />
    </Modal>
  );
}

function RadioPill({
  checked,
  onClick,
  tone,
  children,
}: {
  checked: boolean;
  onClick: () => void;
  tone: "emerald" | "neutral";
  children: React.ReactNode;
}): JSX.Element {
  const base = checked
    ? tone === "emerald"
      ? "bg-emerald-700 text-white border-emerald-700"
      : "bg-neutral-700 text-white border-neutral-700"
    : "bg-white text-neutral-700 border-neutral-300 hover:bg-neutral-50";
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={checked}
      className={`rounded-md border px-3 py-1.5 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ${base}`}
    >
      {children}
    </button>
  );
}
